import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../../../api/client";
import useAuth from "../../../../hooks/useAuth";

// Who else gets the admin's alerts: the WhatsApp / Telegram messages (overdue
// stages, low stock, PO start, breakdown escalations, the daily summary) and
// the daily report emails. The superadmin sets how many people that can be,
// the admin included; the admin picks who. Admin-only, like the audit trail.

const cardStyle = {
  background: "#fff",
  border: "1px solid #e4ebf5",
  borderRadius: 12,
  padding: 20,
  marginBottom: 20,
};
const labelStyle = { display: "block", fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 6 };
const inputStyle = {
  width: "100%",
  padding: "8px 10px",
  border: "1px solid #d6dfed",
  borderRadius: 8,
  fontSize: 14,
  color: "#0f2d5c",
  background: "#fff",
  boxSizing: "border-box",
};
const thStyle = {
  padding: "10px 16px",
  textAlign: "left",
  fontWeight: 700,
  color: "#374151",
  borderBottom: "1px solid #e4ebf5",
  whiteSpace: "nowrap",
  background: "#f8fafc",
};
const tdStyle = { padding: "12px 16px", color: "#0f2d5c", verticalAlign: "middle" };

const CHAT_NAME = { whatsapp: "WhatsApp", telegram: "Telegram" };

function Pill({ tone, children }) {
  const tones = {
    good: { bg: "#dcfce7", color: "#15803d" },
    warn: { bg: "#fff7ed", color: "#c2410c" },
    muted: { bg: "#f1f5f9", color: "#64748b" },
  };
  const t = tones[tone] || tones.muted;
  return (
    <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700, background: t.bg, color: t.color }}>
      {children}
    </span>
  );
}

function ChannelToggle({ checked, disabled, onChange, label, unreachable }) {
  return (
    <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "#0f2d5c", cursor: disabled ? "default" : "pointer" }}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} aria-label={label} />
      {checked ? "On" : "Off"}
      {checked && unreachable && <Pill tone="warn">{unreachable}</Pill>}
    </label>
  );
}

function errorText(err, fallback) {
  return err?.response?.data?.message || err?.response?.data?.error || fallback;
}

export default function AlertRecipients() {
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [pick, setPick] = useState("");

  const load = useCallback(async () => {
    try {
      setError("");
      const res = await api.get("/manufacturing/alert-recipients");
      setData(res.data.data);
    } catch (err) {
      setError(errorText(err, "Failed to load the alert list"));
    }
  }, []);

  useEffect(() => {
    if (user?.isAdmin) load();
  }, [user, load]);

  const run = async (request) => {
    setBusy(true);
    setError("");
    try {
      const res = await request();
      setData(res.data.data);
      return true;
    } catch (err) {
      setError(errorText(err, "Something went wrong"));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!pick) return;
    if (await run(() => api.post("/manufacturing/alert-recipients", { userId: pick }))) setPick("");
  };

  const handleToggle = (row, key, value) =>
    run(() => api.patch(`/manufacturing/alert-recipients/${row.id}`, { [key]: value }));

  const handleRemove = (row) => run(() => api.delete(`/manufacturing/alert-recipients/${row.id}`));

  if (authLoading) return null;
  if (!user?.isAdmin) {
    return (
      <div style={{ padding: 20 }}>
        <Link className="back-link" to="/manufacturing/dashboard">← Back to Dashboard</Link>
        <p style={{ color: "#64748b", marginTop: 20 }}>Only the admin can change who gets alerts.</p>
      </div>
    );
  }

  const chat = CHAT_NAME[data?.provider] || "Chat";
  const full = data && data.slotsLeft === 0;
  const used = data ? 1 + data.recipients.filter((r) => r.active).length : 0;

  return (
    <div style={{ padding: 20, height: "100%", overflow: "auto", background: "#f5f8fc" }}>
      <Link className="back-link" to="/manufacturing/dashboard">← Back to Dashboard</Link>

      <div style={{ margin: "12px 0 20px" }}>
        <h1 style={{ margin: "0 0 4px", color: "#0f2d5c" }}>Alert Recipients</h1>
        <p style={{ margin: 0, color: "#64748b", fontSize: 14, maxWidth: 720 }}>
          Choose who else gets the alerts you get: {chat} messages for overdue stages, low stock, POs starting,
          breakdown escalations and the daily summary, and the end-of-day report emails. Subscription reminders
          still go to you only.
        </p>
      </div>

      {error && (
        <div style={{ padding: "10px 14px", background: "#fff1f2", border: "1px solid #fecdd3", color: "#be123c", borderRadius: 10, marginBottom: 16, fontSize: 13 }}>
          {error}
        </div>
      )}

      {!data ? (
        <div style={{ ...cardStyle, color: "#94a3b8" }}>{error ? "" : "Loading…"}</div>
      ) : (
        <>
          <div style={{ ...cardStyle, display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.4px" }}>
                Recipients
              </div>
              <div style={{ fontSize: 26, fontWeight: 800, color: "#0f2d5c" }}>
                {used} <span style={{ fontSize: 15, fontWeight: 600, color: "#64748b" }}>of {data.limit}</span>
              </div>
            </div>
            <div style={{ fontSize: 13, color: "#64748b", flex: 1, minWidth: 220 }}>
              {data.limit <= 1
                ? "Your plan sends alerts to the admin only. Contact support to add more people."
                : full
                  ? "The list is full. Remove someone to add another person, or contact support for more."
                  : `You can add ${data.slotsLeft} more ${data.slotsLeft === 1 ? "person" : "people"}. The limit includes you.`}
            </div>
          </div>

          {data.limit > 1 && (
            <form onSubmit={handleAdd} style={cardStyle}>
              <h3 style={{ margin: "0 0 14px", color: "#0f2d5c", fontSize: 15 }}>Add a person</h3>
              <div style={{ display: "flex", gap: 12, alignItems: "end", flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 220, maxWidth: 420 }}>
                  <label style={labelStyle} htmlFor="ar-user">User</label>
                  <select id="ar-user" style={inputStyle} value={pick} onChange={(e) => setPick(e.target.value)} disabled={full || busy}>
                    <option value="">{data.candidates.length ? "Select a user…" : "Everyone is already on the list"}</option>
                    {data.candidates.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                        {c.email ? ` · ${c.email}` : ""}
                        {!c.canMessage ? ` · no ${chat}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <button type="submit" className="btn-primary" disabled={!pick || full || busy} style={{ fontSize: 13 }}>
                  {busy ? "Saving…" : "Add"}
                </button>
              </div>
              <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 8 }}>
                People are reached on the {chat} number and email on their user profile.
              </div>
            </form>
          )}

          <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr>
                    {["Person", `${chat} messages`, "Daily emails", ""].map((h) => (
                      <th key={h || "actions"} style={thStyle}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.admin && (
                    <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 600 }}>
                          {data.admin.name} <Pill tone="good">Admin</Pill>
                        </div>
                        <div style={{ fontSize: 12, color: "#64748b" }}>{data.admin.email}</div>
                      </td>
                      <td style={tdStyle}>
                        <ChannelToggle checked disabled label="Admin messages" unreachable={!data.admin.canMessage && `No ${chat} on profile`} onChange={() => {}} />
                      </td>
                      <td style={tdStyle}>
                        <ChannelToggle checked disabled label="Admin emails" unreachable={!data.admin.canEmail && "No email"} onChange={() => {}} />
                      </td>
                      <td style={{ ...tdStyle, color: "#94a3b8", fontSize: 12 }}>Always included</td>
                    </tr>
                  )}
                  {data.recipients.map((r) => (
                    <tr key={r.id} style={{ borderBottom: "1px solid #f1f5f9", opacity: r.active ? 1 : 0.6 }}>
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 600 }}>
                          {r.user.name}{" "}
                          {r.reason === "over_limit" && <Pill tone="warn">Over limit, not receiving</Pill>}
                          {r.reason === "deactivated" && <Pill>Deactivated</Pill>}
                        </div>
                        <div style={{ fontSize: 12, color: "#64748b" }}>{r.user.email}</div>
                      </td>
                      <td style={tdStyle}>
                        <ChannelToggle
                          checked={r.messages}
                          disabled={busy}
                          label={`${chat} messages for ${r.user.name}`}
                          unreachable={!r.user.canMessage && `No ${chat} on profile`}
                          onChange={(v) => handleToggle(r, "messages", v)}
                        />
                      </td>
                      <td style={tdStyle}>
                        <ChannelToggle
                          checked={r.emails}
                          disabled={busy}
                          label={`Daily emails for ${r.user.name}`}
                          unreachable={!r.user.canEmail && "No email"}
                          onChange={(v) => handleToggle(r, "emails", v)}
                        />
                      </td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>
                        <button
                          type="button"
                          onClick={() => handleRemove(r)}
                          disabled={busy}
                          style={{ padding: 0, border: "none", background: "none", color: "#dc2626", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                  {data.recipients.length === 0 && (
                    <tr>
                      <td colSpan={4} style={{ ...tdStyle, color: "#94a3b8", textAlign: "center", padding: 24 }}>
                        Nobody else gets alerts yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
